import { createAsyncThunk } from '@reduxjs/toolkit';
import { ThunkDispatch } from 'redux-thunk';

import { mimeTypes, ROOT_FOLDER_ID } from 'utilities/constants';
import { findNodeParentKey } from 'utilities/documentTree';
import {
    createSharedDownloadUnavailableError,
    isHtmlResponseContentType,
    normalizeSharedDownloadRequestError,
} from 'utilities/shared-download';
import type { RootState } from '../store';
import { getRejectedValues, useAxios } from '../utilities/axios';

// Matches useAppDispatch; the store types its middleware as `any`, so the store's
// own AppDispatch has no thunk overload.
type DocumentsDispatch = ThunkDispatch<RootState, unknown, any>;

const getSharedDownloadApiBaseUrl = (): string => {
    const apiPort = process.env.REACT_APP_API_PORT ? `:${process.env.REACT_APP_API_PORT}` : '';

    return `${window.location.protocol}//${window.location.hostname}${apiPort}`;
};

export const fetchDocument = createAsyncThunk('document/fetch', async (id: any, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();

        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

        const response = await axios.get(`api/v1/documents/${id}`);

        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});

export const fetchDocumentVersions = createAsyncThunk(
    'document/versions',
    async (id: any, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.get(`api/v1/documents/${id}/versions`);

            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const fetchDocumentVersion = createAsyncThunk(
    'document/fetchVersion',
    async (data: { id: any; version: any; }, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.get(`api/v1/documents/${data.id}/versions/${data.version}`);

            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const openDocumentVersion = createAsyncThunk(
    'document/openVersion',
    async (data: { id: any; version: any; }, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.get(`api/v1/documents/${data.id}/versions/${data.version}/open`);

            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const setDocumentVersion = createAsyncThunk(
    'document/setVersion',
    async (data: { id: any; version: any; }, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.put(`api/v1/documents/${data.id}/versions/`, {
                version_id: data.version,
            });

            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const copyDocumentVersion = createAsyncThunk(
    'document/copyVersion',
    async (data: { id: any; version: any; }, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();
            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.post(`api/v1/documents/${data.id}/versions/copy`, {
                version_id: data.version,
            });

            return response.data;
        }
        catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const deleteDocumentVersion = createAsyncThunk(
    'document/deleteVersion',
    async (data: { id: any; version: any; }, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.delete(`api/v1/documents/${data.id}/versions/${data.version}`);

            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);


export const fetchMarkup = createAsyncThunk('document/fetchMarkup', async (id: any, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();
        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

        const response = await axios.get(`api/v1/documents/${id}/markup`);

        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});

// deprecated
export const saveMarkup = createAsyncThunk(
    'document/saveMarkup',
    async ({ id, data }: any, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();
            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.post(`api/v1/documents/${id}/markup`, data);

            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const importMarkup = createAsyncThunk(
    'document/importMarkup',
    async (id: any, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();
            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.post(`api/v1/documents/${id}/markup/import`);

            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const saveMarkupAsVersion = createAsyncThunk(
    'document/saveMarkupAsVersion',
    async (id: string, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();
            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.post(`api/v1/documents/${id}/markup/version`);

            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const fetchDocuments = createAsyncThunk(
    'document/list',
    async (parent_id: any | null = null, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();
            const params = state.documents.params;

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            let queryParams: any = {};
            if (parent_id) queryParams.parent_id = parent_id;
            if (params.query) queryParams.query = params.query;
            if (params.filters?.type && params.filters?.type && params.filters?.type?.value.length) {
                let mime_types: Array<string> = [];
                Object.values(params.filters?.type?.value).forEach((type: any) => {
                    mime_types.push(...mimeTypes[type.id as keyof typeof mimeTypes]);
                });
                queryParams.mime_type = mime_types.join(',');
            }
            if (params.filters?.created_by && params.filters?.created_by && params.filters?.created_by?.value.length)
                queryParams.created_by = params.filters.created_by.value
                    .map(function (el: any) {
                        return el.id;
                    })
                    .join(',');
            if (params.filters?.updated_by && params.filters?.updated_by?.value?.length)
                queryParams.updated_by = params.filters.updated_by.value.map((el: any) => el.id).join(',');
            if (params.filters?.modified_before && params.filters?.modified_before?.value)
                queryParams.modified_before = params.filters.modified_before.value;
            if (params.filters?.modified_after && params.filters?.modified_after?.value)
                queryParams.modified_after = params.filters.modified_after.value;
            if (params.filters?.created_before && params.filters?.created_before?.value)
                queryParams.created_before = params.filters.created_before.value;
            if (params.filters?.created_after && params.filters?.created_after?.value)
                queryParams.created_after = params.filters.created_after.value;
            if (params.filters?.has_versions && params.filters?.has_versions?.value !== undefined)
                queryParams.has_versions = params.filters.has_versions.value ? 'true' : 'false';
            if (params.filters?.logic) queryParams.logic = params.filters.logic;
            if (
                params.filters?.in_descendants_of &&
                params.filters?.in_descendants_of?.value !== undefined &&
                params.filters?.in_descendants_of?.value !== null
            ) {
                queryParams.in_descendants_of = params.filters.in_descendants_of.value;
                delete queryParams.parent_id;
            }

            const response = await axios.get('api/v1/list/all', { params: queryParams });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const fetchDocumentTree = createAsyncThunk('document/tree', async (_, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();

        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

        const response = await axios.get('api/v1/list/documentTree', {});
        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});

// Lazily load the direct children of a single tree node (parentId === 0 reloads the root level).
export const fetchDocumentTreeChildren = createAsyncThunk(
    'document/treeChildren',
    async ({ parentId }: { parentId: number }, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.get('api/v1/list/documentTree', {
                params: { parent_id: parentId },
            });
            return {
                parentId,
                children: response.data.data.documentTree,
                parentTitle: response.data.data.parentTitle ?? null,
            };
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

// Fetch the ancestor chain + each level along it so the tree can auto-expand to a node in one request.
export const fetchDocumentTreePath = createAsyncThunk(
    'document/treePath',
    async ({ nodeId }: { nodeId: number }, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.get('api/v1/list/documentTreePath', {
                params: { node_id: nodeId },
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

/**
 * Refresh the tree levels a change to `changedFolders` can show up in. Pass the
 * folders whose contents or label changed.
 *
 * Each one needs two levels re-fetched: its own children, plus its parent's level,
 * which is where the backend puts that folder's "(count)" label and its isLeaf flag
 * (CategoryRepositoryService::buildLevel). The count includes files, so uploads and
 * file deletes/moves change the tree too. Folders absent from the loaded tree are
 * skipped — nothing is displaying them.
 */
export const refreshDocumentTreeLevels =
    (changedFolders: Array<number | null | undefined>) =>
    (dispatch: DocumentsDispatch, getState: () => RootState) => {
        const tree = getState().documents.documentTree ?? [];
        const levels = new Set<number>();

        new Set(changedFolders.map((id) => Number(id ?? ROOT_FOLDER_ID))).forEach((key) => {
            const location = findNodeParentKey(tree, key);
            if (!location) return;

            levels.add(key);
            if (location.parentKey !== null) levels.add(location.parentKey);
        });

        levels.forEach((parentId) => dispatch(fetchDocumentTreeChildren({ parentId })));
    };

export const fetchStorageCounters = createAsyncThunk(
    'document/fetchStorageCounters',
    async (_, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            let params: any = {};

            const response = await axios.get('api/v1/list/storage/counters', { params: params });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const fetchStorageSize = createAsyncThunk(
    'document/fetchStorageSize',
    async (_, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.get('api/v1/storage/size');
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const fetchDeletedDocuments = createAsyncThunk(
    'document/fetchDeleted',
    async (_, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            let params: any = {};

            const response = await axios.get('api/v1/list/trash', { params: params });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const fetchSharedDocuments = createAsyncThunk(
    'document/fetchShared',
    async (parent_id: any | null = null, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            let params: any = {};
            if (parent_id) params.parent_id = parent_id;

            const response = await axios.get('api/v1/list/shared', { params: params });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const doesDocumentExist = createAsyncThunk(
    'document/doesExist',
    async (data: any, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.post('api/v1/documents/exists', data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const uploadDocument = createAsyncThunk('document/upload', async (data: any, { getState, rejectWithValue, dispatch }) => {
    try {
        const state: any = getState();

        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

        const response = await axios.post('api/v1/documents/upload', data);
        dispatch(fetchStorageSize());
        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});

export const updateDocument = createAsyncThunk(
    'document/update',
    async (data: { id?: string; file: any; }, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.post(`api/v1/documents/${data.id}/update`, data.file);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const renameDocument = createAsyncThunk(
    'document/rename',
    async (data: { id: string; name: string; }, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.post(`api/v1/documents/${data.id}/rename`, data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const moveDocument = createAsyncThunk(
    'document/move',
    async (arg: { id: string; category_id: number | null; suppressError?: boolean; }, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const { suppressError, ...data } = arg;
            const response = await axios.post(`api/v1/documents/${data.id}/move`, data);

            return response.data;
        } catch (error: any) {
            const errorPayload = getRejectedValues(error);

            if (arg.suppressError) {
                errorPayload.suppressError = true;
            }

            return rejectWithValue(errorPayload);
        }
    },
);

export const copyDocument = createAsyncThunk(
    'document/copy',
    async (data: { id: string; category_id: number | null; }, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.post(`api/v1/documents/${data.id}/copy`, data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const softDeleteDocument = createAsyncThunk(
    'document/softDelete',
    async (id: any, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.delete(`api/v1/documents/${id}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const foreverDeleteDocument = createAsyncThunk(
    'document/foreverDelete',
    async (id: any, { getState, rejectWithValue, dispatch }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.delete(`api/v1/documents/${id}/forever`);
            dispatch(fetchStorageSize());
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const restoreDeletedDocument = createAsyncThunk(
    'document/restoreDeleted',
    async (id: any, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.post(`api/v1/documents/${id}/restore`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const foreverDeleteTrashed = createAsyncThunk(
    'document/foreverDeleteTrashed',
    async (_, { getState, rejectWithValue, dispatch }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.delete(`api/v1/list/trash/forever`);
            dispatch(fetchStorageSize());
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const fetchUsers = createAsyncThunk('document/fetchUsers', async (id: any, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();

        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

        const response = await axios.get(`api/v1/documents/${id}/users`);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});

export const shareDocuments = createAsyncThunk('document/share', async (data: any, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();

        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

        const response = await axios.post(`api/v1/documents/share`, data);

        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});

export const downloadDocument = createAsyncThunk(
    'document/download',
    async (documentInfo: { id: string; name: string; }, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.get(`api/v1/documents/${documentInfo.id}/download`, {
                responseType: 'blob',
            });

            const contentType = response.headers['content-type'] || 'application/octet-stream';
            const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
            const link = document.createElement('a');
            link.href = url;

            const contentDisposition = response.headers['content-disposition'];
            let filename = documentInfo.name;

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename\*?="?([^";]+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1].trim();
                    filename = filename.replace(/^["']|["']$/g, '');
                }
            }

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return { success: true, filename };
        } catch (error: any) {
            const data = await error.response.data.text();
            return rejectWithValue({
                status: error.response.status,
                data: JSON.parse(data),
            });
        }
    },
);

export const downloadDocumentVersion = createAsyncThunk(
    'document/downloadVersion',
    async (data: { id: string; version: string; name: string; }, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.get(`api/v1/documents/${data.id}/versions/${data.version}/download`, {
                responseType: 'blob',
            });

            const contentType = response.headers['content-type'] || 'application/octet-stream';
            const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
            const link = document.createElement('a');
            link.href = url;

            const contentDisposition = response.headers['content-disposition'];
            let filename = data.name;

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename\*?="?([^";]+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1].trim();
                    filename = filename.replace(/^["']|["']$/g, '');
                }
            }

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return { success: true, filename };
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const bulkDownload = createAsyncThunk(
    'documents/downloadBulk',
    async (
        data: {
            entities?: string[];
            filename?: string | null;
        },
        { getState, rejectWithValue },
    ) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.post(
                'api/v1/documents/bulk-download',
                {
                    entities: data.entities || [],
                },
                {
                    responseType: 'blob',
                }
            );
            if (response.status === 202) {
                return {
                    signature: JSON.parse(await response.data.text()).signature,
                }
            }

            const contentType = response.headers['content-type'] || 'application/zip';
            const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
            const link = document.createElement('a');
            link.href = url;

            const contentDisposition = response.headers['content-disposition'];
            let filename = data.filename || '';
            if (contentDisposition && !filename) {
                const filenameMatch = contentDisposition.match(/filename\*?="?([^";]+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1].trim();
                    filename = filename.replace(/^["']|["']$/g, '');
                }
            }
            if (!filename) {
                const currentDate = new Date().toISOString().split('T')[0];
                filename = `Documents_${currentDate}.zip`;
            }

            link.setAttribute('download', filename);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return {
                success: true,
                filename,
                count: data.entities?.length || 0,
            };
        } catch (error: any) {
            const data = await error.response.data.text();
            return rejectWithValue({
                status: error.response.status,
                data: JSON.parse(data),
            });
        }
    },
);

export const downloadSharedDocuments = createAsyncThunk(
    'documents/downloadShared',
    async (token: string, { rejectWithValue }) => {
        try {
            const axios = useAxios();
            const response = await axios.get(`${getSharedDownloadApiBaseUrl()}/api/v1/guest/documents`, {
                params: { token },
                responseType: 'blob',
            });

            if (response.status === 202) {
                return {
                    signature: JSON.parse(await response.data.text()).signature,
                }
            }

            const contentType = response.headers['content-type'] || 'application/octet-stream';
            if (isHtmlResponseContentType(contentType)) {
                return rejectWithValue(createSharedDownloadUnavailableError(response.status));
            }

            const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
            const link = document.createElement('a');

            link.href = url;

            const contentDisposition = response.headers['content-disposition'];
            let filename = 'shared-files.zip';

            if (contentDisposition) {
                const lower = contentDisposition.toLowerCase();
                const key = 'filename=';
                const idx = lower.indexOf(key);

                if (idx >= 0) {
                    filename = contentDisposition.substring(idx + key.length).trim();
                }
            }

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return { success: true, filename };
        } catch (error: any) {
            return rejectWithValue(await normalizeSharedDownloadRequestError(error));
        }
    },
);

export const getSharedDownloadAvailability = createAsyncThunk(
    'documents/getSharedDownloadAvailability',
    async (token: string, { rejectWithValue }) => {
        try {
            const axios = useAxios();
            const response = await axios.get(`${getSharedDownloadApiBaseUrl()}/api/v1/guest/documents/status`, {
                params: { token },
            });

            if (typeof response.data?.available !== 'boolean') {
                return rejectWithValue(createSharedDownloadUnavailableError(response.status));
            }

            return response.data as { available: boolean; message?: string | null };
        } catch (error: any) {
            return rejectWithValue(await normalizeSharedDownloadRequestError(error));
        }
    },
);

// note this function handles deletion of both documents and categories
export const bulkSoftDelete = createAsyncThunk(
    'documents/bulkSoftDelete',
    async (ids: (string | number)[], { getState, rejectWithValue }) => {
        try {
            const state: any = getState();
            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.delete(`api/v1/documents?ids=${ids.join(',')}`);
            return response.data;
        } catch (error: any) {
            const data = await error.response.data.text();
            return rejectWithValue({
                status: error.response.status,
                data: JSON.parse(data),
            });
        }
    },
);

export const fetchDownloads = createAsyncThunk(
    'downloads/fetchDownloads',
    async (_, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const userId = state.account.user?.id;
            if (!userId) {
                throw new Error('User ID not found');
            }

            const response = await axios.get(`api/v1/documents/downloads`, {
                params: { identifier: userId }
            });

            return response.data;
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch downloads';
            return rejectWithValue({
                status: error.response?.status || 500,
                data: {
                    message: errorMessage,
                    ...error.response?.data
                },
            });
        }
    },
);

export const downloadFile = createAsyncThunk(
    'downloads/downloadFile',
    async (signature: string, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();

            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.get(`api/v1/documents/downloads/${signature}`, {
                responseType: 'blob',
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            // Extract filename from headers or use default
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'download.zip';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename\*?="?([^";]+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1].trim().replace(/^["']|["']$/g, '');
                }
            }

            link.setAttribute('download', filename);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return { signature, success: true };
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to download file';
            return rejectWithValue({
                status: error.response?.status || 500,
                data: {
                    message: errorMessage,
                    ...error.response?.data
                },
                signature,
            });
        }
    },
);

export const deleteDownload = createAsyncThunk(
    'downloads/deleteDownload',
    async (signature: string, { getState, rejectWithValue }) => {
        const state: any = getState();

        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

        try {
            const response = await axios.delete(`api/v1/documents/downloads/${signature}`);
            return { signature, success: true };
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to delete download';
            return rejectWithValue({
                status: error.response?.status || 500,
                data: {
                    message: errorMessage,
                    ...error.response?.data
                },
                signature,
            });
        }
    },
);

export const getDownloadStatus = createAsyncThunk(
    'downloads/getStatus',
    async ({ signature, token }: { signature: string; token?: string }, { rejectWithValue }) => {
        try {
            const axios = useAxios();

            const response = await axios.get(`${getSharedDownloadApiBaseUrl()}/api/v1/documents/downloads/${signature}`, {
                params: token ? { token } : undefined,
            });

            if (isHtmlResponseContentType(response.headers['content-type'])) {
                return rejectWithValue(createSharedDownloadUnavailableError(response.status));
            }

            return response.data;
        } catch (error: any) {
            return rejectWithValue({
                ...(await normalizeSharedDownloadRequestError(error)),
                signature,
            });
        }
    },
);

/**
 * Permanent public link management (authenticated, files manage permission).
 */
export const getPublicLink = createAsyncThunk(
    'document/publicLink/get',
    async (documentId: string, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();
            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.get(`api/v1/documents/${documentId}/public-link`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const createPublicLink = createAsyncThunk(
    'document/publicLink/create',
    async (documentId: string, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();
            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.post(`api/v1/documents/${documentId}/public-link`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);

export const deletePublicLink = createAsyncThunk(
    'document/publicLink/delete',
    async (documentId: string, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();
            if (!state.auth.accessToken) return;
            if (!state.tenants.currentTenant) return;
            const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

            const response = await axios.delete(`api/v1/documents/${documentId}/public-link`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getRejectedValues(error));
        }
    },
);
